import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
import { CreditCard, History, CheckCircle, XCircle, Clock, TrendingUp, TrendingDown, RefreshCw, ShieldAlert } from 'lucide-react';
import { adminGetAllPlanLogs, adminGetAllPaymentRequests, adminGetActionLogs } from './AdminPanel';

const PAYMENT_STATUS = {
  pending:   { label: '대기중',    color: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20', icon: Clock },
  confirmed: { label: '확인 완료', color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',   icon: CheckCircle },
  rejected:  { label: '거절됨',    color: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',           icon: XCircle },
};

const ACCOUNT_ACTION_LABELS = {
  approve_account: { label: '계정 승인',   color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' },
  suspend_account: { label: '계정 정지',   color: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20' },
  restore_account: { label: '계정 복구',   color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' },
  delete_gym:      { label: '헬스장 삭제', color: 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30' },
  update_trial:    { label: '체험 상태 변경', color: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20' },
};

const ACTION_LABELS = {
  extend:      { label: '기간 연장', icon: TrendingUp,   color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' },
  reduce:      { label: '기간 단축', icon: TrendingDown, color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20' },
  plan_change: { label: '플랜 변경', icon: RefreshCw,    color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' },
};

const PLAN_LABELS = { trial: '무료체험', active: '이용중', suspended: '정지', null: '-' };

function fmt(dateStr) {
  if (!dateStr) return '-';
  try { return format(parseISO(dateStr), 'yyyy.MM.dd HH:mm', { locale: ko }); } catch { return '-'; }
}

function fmtDate(dateStr) {
  if (!dateStr) return '-';
  try { return format(parseISO(dateStr), 'yyyy.MM.dd', { locale: ko }); } catch { return '-'; }
}

export default function AdminLogs() {
  const [tab, setTab] = useState('payments');
  const [payments, setPayments] = useState([]);
  const [planLogs, setPlanLogs] = useState([]);
  const [actionLogs, setActionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentFilter, setPaymentFilter] = useState('all');

  useEffect(() => {
    Promise.all([adminGetAllPaymentRequests(), adminGetAllPlanLogs(), adminGetActionLogs()]).then(([p, l, a]) => {
      setPayments(p || []);
      setPlanLogs(l || []);
      setActionLogs(a || []);
      setLoading(false);
    });
  }, []);

  const filteredPayments = paymentFilter === 'all'
    ? payments
    : payments.filter(p => p.status === paymentFilter);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-5xl">
      <h1 className="text-xl font-bold text-gray-900 dark:text-white">활동 로그</h1>

      {/* 탭 */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setTab('payments')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'payments'
              ? 'border-red-600 text-red-600 dark:text-red-400 dark:border-red-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          납부 내역
          <span className="ml-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-full">
            {payments.length}
          </span>
        </button>
        <button
          onClick={() => setTab('plans')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'plans'
              ? 'border-red-600 text-red-600 dark:text-red-400 dark:border-red-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <History className="w-4 h-4" />
          플랜 변경 이력
          <span className="ml-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-full">
            {planLogs.length}
          </span>
        </button>
        <button
          onClick={() => setTab('actions')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'actions'
              ? 'border-red-600 text-red-600 dark:text-red-400 dark:border-red-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <ShieldAlert className="w-4 h-4" />
          계정/기타 변경
          <span className="ml-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-full">
            {actionLogs.length}
          </span>
        </button>
      </div>

      {/* 납부 내역 탭 */}
      {tab === 'payments' && (
        <div className="card overflow-hidden">
          {/* 필터 */}
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex gap-2">
            {[
              { key: 'all', label: '전체' },
              { key: 'pending', label: '대기중' },
              { key: 'confirmed', label: '확인 완료' },
              { key: 'rejected', label: '거절됨' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setPaymentFilter(key)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  paymentFilter === key
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {label}
                {key !== 'all' && (
                  <span className="ml-1 opacity-75">({payments.filter(p => p.status === key).length})</span>
                )}
              </button>
            ))}
          </div>

          {filteredPayments.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">내역이 없습니다.</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredPayments.map(req => {
                const cfg = PAYMENT_STATUS[req.status] || PAYMENT_STATUS.pending;
                const Icon = cfg.icon;
                return (
                  <div key={req.id} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{req.gym_name}</p>
                          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
                            <Icon className="w-3 h-3" />{cfg.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {req.owner_name} · {req.owner_email}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {req.period_months}개월 연장 · 입금자: <span className="font-medium text-gray-700 dark:text-gray-300">{req.sender_name}</span>
                          {req.note && <span className="text-gray-400"> · "{req.note}"</span>}
                        </p>
                        {req.admin_note && (
                          <p className="text-xs text-gray-400 mt-0.5">관리자 메모: {req.admin_note}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-400">요청: {fmt(req.created_at)}</p>
                        {req.confirmed_at && (
                          <p className="text-xs text-gray-400 mt-0.5">처리: {fmt(req.confirmed_at)}</p>
                        )}
                        <p className="text-sm font-bold text-gray-900 dark:text-white mt-1">
                          {(49000 * req.period_months).toLocaleString('ko-KR')}원
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 플랜 변경 이력 탭 */}
      {tab === 'plans' && (
        <div className="card overflow-hidden">
          {planLogs.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">변경 이력이 없습니다.</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {planLogs.map(log => {
                const act = ACTION_LABELS[log.action] || ACTION_LABELS.plan_change;
                const ActIcon = act.icon;
                return (
                  <div key={log.id} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{log.gym_name}</p>
                          <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${act.color}`}>
                            <ActIcon className="w-3 h-3" />{act.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          처리자: <span className="font-medium">{log.admin_email || '-'}</span>
                        </p>
                        {(log.old_plan || log.new_plan) && log.old_plan !== log.new_plan && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            플랜: {PLAN_LABELS[log.old_plan] || log.old_plan} → {PLAN_LABELS[log.new_plan] || log.new_plan}
                          </p>
                        )}
                        {(log.old_expires_at || log.new_expires_at) && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            만료일: {fmtDate(log.old_expires_at)} → <span className="font-medium text-gray-700 dark:text-gray-300">{fmtDate(log.new_expires_at)}</span>
                          </p>
                        )}
                        {log.note && (
                          <p className="text-xs text-gray-400 mt-0.5 italic">"{log.note}"</p>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 flex-shrink-0">{fmt(log.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* 계정/기타 변경 탭 */}
      {tab === 'actions' && (
        <div className="card overflow-hidden">
          {actionLogs.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">변경 이력이 없습니다.</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {actionLogs.map(log => {
                const cfg = ACCOUNT_ACTION_LABELS[log.action] || { label: log.action, color: 'text-gray-600 bg-gray-100' };
                return (
                  <div key={log.id} className="px-5 py-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {log.target_name && (
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">{log.target_name}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        처리자: <span className="font-medium">{log.admin_email || '-'}</span>
                      </p>
                      {(log.old_value || log.new_value) && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {log.old_value} → <span className="font-medium text-gray-600 dark:text-gray-300">{log.new_value}</span>
                        </p>
                      )}
                      {log.note && <p className="text-xs text-gray-400 mt-0.5 italic">"{log.note}"</p>}
                    </div>
                    <p className="text-xs text-gray-400 flex-shrink-0">{fmt(log.created_at)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
